import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { Subscription, Observable } from 'rxjs';
import { Prescription } from '@models/prescription';
import { Patient } from '@models/patient';
import { PrescriptionForm } from '@classes/prescription-form';
import { AuthService } from '@services/auth.service';
import { NotificationService } from '@services/notification.service';
import { PrescriptionService } from '@services/prescription.service';
import { SettingsService } from '@services/settings.service';
import {
  RX_NAME_SUGGESTIONS,
  RX_USE_TEMPLATES,
  formatRxSelectorLabel,
  rxDoctorName,
  rxExpirationDate,
  rxStatusLabel,
} from '../../../../constants/rx-names';
import { RxHistoryDialogComponent } from '@components/admin/order/task-popups/rx-history-dialog.component';
import { RxConfirmDialogComponent, RxImageViewerDialogComponent } from '@components/admin/order/task-popups/rx-confirm-dialog.component';
import { EmailOrderDialogComponent } from '@components/admin/patient/email-order-dialog/email-order-dialog.component';
import { exclusiveJobAction, jobActionList, toggleJobAction } from '@classes/job-actions';
import {
  CompleteTaskInput,
  WorkflowContext,
  WorkflowEntry,
  WorkflowResult,
  activityEntryDefaults,
  awaitingActions,
  awaitingRows,
  canonicalOpenStatus,
  completeOpenTask,
  isValidInitials,
  normalizeInitials,
  openTaskActions,
  openTaskRows,
  resolveAwaiting,
} from '@classes/task-activity';

@Component({
  selector: 'app-prescription-details',
  templateUrl: './prescription-details.component.html',
  styleUrls: ['./prescription-details.component.css', '../order.component.css'],
  host: { class: 'ao-fields' },
})
export class PrescriptionDetailsComponent implements OnInit, OnDestroy {
  @Input() orderForm: FormGroup;
  @Input() patient: Patient;
  @Output() saveOrder = new EventEmitter<{ destination: string }>();

  formGroup: FormGroup;
  rxNameSuggestions = RX_NAME_SUGGESTIONS;
  rxUseTemplates = RX_USE_TEMPLATES;
  rxNameControl = new FormControl('');
  newNameControl = new FormControl('');
  patientPrescriptions: Prescription[] = [];
  sourceImageUrl = '';
  extractedUnverified = false;
  verified = false;
  loadingHistory = false;
  pendingRxTask: {
    kind: 'open' | 'awaiting';
    index: number;
    entry: WorkflowEntry;
    label: string;
    actions: string[];
  } | null = null;
  taskWhoToCall = new FormControl('');
  taskStatusChoice = new FormControl('');
  taskInitials = new FormControl('');
  whoToCallOptions: string[] = [];
  private formSubscriptions = new Subscription();
  private orderSubscriptions = new Subscription();
  private ignoreVerifyReset = false;
  private imageViewerRef: MatDialogRef<RxImageViewerDialogComponent> | null = null;

  constructor(
    private fb: FormBuilder,
    private dialog: MatDialog,
    private auth: AuthService,
    private notificationService: NotificationService,
    private prescriptionService: PrescriptionService,
    private settingsService: SettingsService
  ) {}

  ngOnInit(): void {
    this.whoToCallOptions = this.settingsService.activeWhoToCallOptions();
    this.bindPrescriptionForm(
      PrescriptionForm.initPrescriptionFormGroup(new Prescription())
    );
    this.rxNameControl.setValue(this.formGroup.get('type')?.value || '');
    this.taskInitials.setValue(this.defaultInitials);
    this.loadPatientPrescriptions();
    this.refreshPendingRxTask();
    const savedId = String(this.orderForm?.get('prescriptionId')?.value || '');
    const savedName = String(this.orderForm?.get('rxName')?.value || '');
    if (savedName) {
      this.rxNameControl.setValue(savedName);
    }
    if (savedId) {
      this.formGroup.get('_id')?.setValue(savedId);
    }
    const activity = this.activityFormArray;
    if (activity) {
      this.orderSubscriptions.add(
        activity.valueChanges.subscribe(() => this.refreshPendingRxTask())
      );
    }
  }

  ngOnDestroy(): void {
    this.formSubscriptions.unsubscribe();
    this.orderSubscriptions.unsubscribe();
    this.imageViewerRef?.close();
  }

  get activePrescriptions(): Prescription[] {
    return (this.patientPrescriptions || []).filter(
      (rx) => rxStatusLabel(rx) === 'ACTIVE'
    );
  }

  get filteredRxNames(): string[] {
    const query = String(this.rxNameControl.value || this.newNameControl.value || '').toUpperCase();
    const names = new Set(this.rxNameSuggestions);
    this.patientPrescriptions.forEach((rx) => {
      const name = String(rx.type || '').trim();
      if (name) {
        names.add(name);
      }
    });
    return Array.from(names).filter((name) => !query || name.includes(query));
  }

  get canSaveRx(): boolean {
    if (!isValidInitials(this.orderForm?.get('initials')?.value)) {
      return false;
    }
    return !(this.extractedUnverified && !this.verified);
  }

  get hasPendingRxTask(): boolean {
    return !!this.pendingRxTask;
  }

  get showProminentNotes(): boolean {
    return this.hasPendingRxTask;
  }

  get rxUseSelection(): string {
    const id = String(this.formGroup?.get('_id')?.value || '').trim();
    if (id) {
      return id;
    }
    const type = String(
      this.rxNameControl.value || this.formGroup?.get('type')?.value || ''
    ).trim();
    if (this.rxUseTemplates.includes(type)) {
      return `template:${type}`;
    }
    return '';
  }

  get defaultInitials(): string {
    return normalizeInitials(
      this.orderForm?.get('initials')?.value ||
        this.auth?.currentUser?.initials ||
        this.auth?.initials ||
        ''
    );
  }

  rxUseLabel(rx: Prescription): string {
    return formatRxSelectorLabel(rx);
  }

  startNewPrescription(): void {
    this.ignoreVerifyReset = true;
    this.bindPrescriptionForm(
      PrescriptionForm.initPrescriptionFormGroup(new Prescription())
    );
    this.rxNameControl.setValue('', { emitEvent: false });
    this.newNameControl.setValue('');
    this.sourceImageUrl = '';
    this.formGroup.get('sourceImageDataUrl')?.setValue('');
    this.extractedUnverified = false;
    this.verified = false;
    this.orderForm?.get('prescriptionId')?.setValue('');
    this.orderForm?.get('rxName')?.setValue('');
    setTimeout(() => (this.ignoreVerifyReset = false));
  }

  onSelectExistingRx(prescriptionId: string): void {
    if (!prescriptionId) {
      this.startNewPrescription();
      return;
    }
    if (String(prescriptionId).startsWith('template:')) {
      const name = String(prescriptionId).slice('template:'.length);
      this.startNewPrescription();
      this.rxNameControl.setValue(name, { emitEvent: false });
      this.formGroup.get('type')?.setValue(name);
      this.orderForm?.get('rxName')?.setValue(name);
      return;
    }
    const rx = this.patientPrescriptions.find(
      (item) => String(item._id) === String(prescriptionId)
    );
    if (!rx) {
      return;
    }
    this.applyPrescription(rx);
  }

  openRxHistory(): void {
    this.dialog
      .open(RxHistoryDialogComponent, {
        width: '720px',
        data: { prescriptions: this.patientPrescriptions },
      })
      .afterClosed()
      .subscribe((rx) => {
        if (rx) {
          this.applyPrescription(rx);
        }
      });
  }

  onUploadSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input?.files?.[0];
    if (!file) {
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      this.sourceImageUrl = String(reader.result || '');
      this.formGroup.get('sourceImageDataUrl')?.setValue(this.sourceImageUrl);
      this.extractedUnverified = true;
      this.verified = false;
      this.openThumbnail();
      this.notificationService.show(
        'success',
        'RX uploaded. Review the original and verify the values before saving.',
        'OK'
      );
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  openThumbnail(): void {
    if (!this.sourceImageUrl) {
      return;
    }
    this.imageViewerRef?.close();
    this.imageViewerRef = this.dialog.open(RxImageViewerDialogComponent, {
      data: { image: this.sourceImageUrl },
      hasBackdrop: false,
      autoFocus: false,
      disableClose: false,
      position: { right: '16px', top: '72px' },
      panelClass: 'rx-image-viewer-panel',
    });
    this.imageViewerRef.afterClosed().subscribe(() => {
      this.imageViewerRef = null;
    });
  }

  onVerifiedChange(checked: boolean): void {
    this.verified = checked;
    if (checked) {
      this.imageViewerRef?.close();
    }
  }

  clearUploadedImage(): void {
    this.sourceImageUrl = '';
    this.formGroup.get('sourceImageDataUrl')?.setValue('');
    this.extractedUnverified = false;
    this.verified = false;
    this.imageViewerRef?.close();
  }

  emailRx(): void {
    const id = String(this.formGroup.get('_id')?.value || '').trim();
    if (!id) {
      this.notificationService.show(
        'error',
        'Save the RX before emailing a copy.',
        'OK'
      );
      return;
    }
    this.dialog
      .open(EmailOrderDialogComponent, {
        width: '420px',
        data: {
          email: this.patient?.email || '',
          invoiceNumber: this.orderForm?.get('rxName')?.value || 'RX',
        },
      })
      .afterClosed()
      .subscribe((email: string) => {
        if (!email) {
          return;
        }
        this.prescriptionService.generatePdf(id).subscribe({
          next: (blob) => {
            const url = window.URL.createObjectURL(blob);
            window.open(url, '_blank');
            const subject = encodeURIComponent('Your prescription copy');
            const body = encodeURIComponent(
              'Please find a copy of your prescription attached.'
            );
            window.location.href = `mailto:${encodeURIComponent(
              email
            )}?subject=${subject}&body=${body}`;
          },
          error: () => {
            this.notificationService.show(
              'error',
              'Could not generate the RX PDF.',
              'OK'
            );
          },
        });
      });
  }

  printRx(): void {
    const id = String(this.formGroup.get('_id')?.value || '').trim();
    if (id) {
      this.prescriptionService.generatePdf(id).subscribe({
        next: (blob) => {
          const url = window.URL.createObjectURL(blob);
          window.open(url, '_blank');
        },
        error: () => this.printUploadedImage(),
      });
      return;
    }
    this.printUploadedImage();
  }

  onSave(destination: string): void {
    if (destination === 'VOID RX') {
      this.voidCurrentRx();
      return;
    }
    if (destination === 'DELETE RX') {
      this.deleteCurrentRx();
      return;
    }
    if (destination === 'VOID') {
      this.saveOrder.emit({ destination });
      return;
    }
    if (this.formGroup.get('voided')?.value) {
      this.notificationService.show(
        'error',
        'This RX is voided and cannot be used on a new job.',
        'OK'
      );
      return;
    }
    if (!this.canSaveRx) {
      this.notificationService.show(
        'error',
        'Enter initials and acknowledge the original RX before saving.',
        'OK'
      );
      return;
    }
    this.syncRxOntoOrder();
    this.persistPrescriptionRecord(() => {
      this.maybeCompleteNeedsRxTask(() => {
        this.saveOrder.emit({ destination: 'SAVE' });
      });
    });
  }

  savePendingRxTask(): void {
    if (!this.pendingRxTask) {
      return;
    }
    const initials = normalizeInitials(
      this.taskInitials.value || this.defaultInitials
    );
    if (!isValidInitials(initials)) {
      this.notificationService.show(
        'error',
        'ENTER VALID EMPLOYEE INITIALS TO SAVE',
        'OK'
      );
      return;
    }
    this.taskInitials.setValue(initials);
    this.orderForm?.get('initials')?.setValue(initials);
    const choice = String(this.taskStatusChoice.value || '').trim();
    if (!choice && this.pendingRxTask.kind === 'open') {
      this.notificationService.show('error', 'SELECT AN ACTIVITY', 'OK');
      return;
    }
    if (!choice && this.pendingRxTask.kind === 'awaiting') {
      this.patchAwaitingExamDate();
      this.saveOrder.emit({ destination: 'SAVE' });
      return;
    }
    const entries = this.currentEntries();
    const input: CompleteTaskInput = {
      id: this.pendingRxTask.entry.id,
      index: this.pendingRxTask.index,
      initials,
      notes: this.formGroup.get('notes')?.value,
      whoToCall: this.taskWhoToCall.value,
      waitingOn: this.pendingRxTask.entry.waitingOn,
      activityChoice: choice,
      followUpDate: this.pendingRxTask.entry.followUpDate,
      expectedDate: this.formGroup.get('examDate')?.value,
    };
    const result =
      this.pendingRxTask.kind === 'open'
        ? completeOpenTask(entries, input, this.workflowContext())
        : resolveAwaiting(entries, input, this.workflowContext());
    if (this.pendingRxTask.kind === 'awaiting' && !result.logItem) {
      this.notificationService.show('error', 'SELECT AN ACTIVITY', 'OK');
      return;
    }
    this.commitTaskResult(result, initials);
  }

  private loadPatientPrescriptions(): void {
    const patientId = String(this.patient?._id || '').trim();
    if (!patientId) {
      return;
    }
    this.loadingHistory = true;
    this.prescriptionService.getPatientPrescriptions(patientId).subscribe({
      next: (prescriptions) => {
        this.patientPrescriptions = prescriptions || [];
        this.loadingHistory = false;
        const savedId = String(this.orderForm?.get('prescriptionId')?.value || '');
        const match = this.patientPrescriptions.find(
          (rx) => String(rx._id) === savedId
        );
        if (match && !this.hasStructuredValues()) {
          this.applyPrescription(match);
        }
      },
      error: () => {
        this.loadingHistory = false;
      },
    });
  }

  private applyPrescription(rx: Prescription): void {
    this.ignoreVerifyReset = true;
    const next = PrescriptionForm.initPrescriptionFormGroup(rx);
    this.bindPrescriptionForm(next);
    this.rxNameControl.setValue(rx.type || '', { emitEvent: false });
    this.newNameControl.setValue('');
    const status = rxStatusLabel(rx);
    if (status === 'ACTIVE' || status === 'HIDDEN') {
      this.orderForm?.get('prescriptionId')?.setValue(rx._id);
      this.orderForm?.get('rxName')?.setValue(rx.type || '');
    } else {
      this.orderForm?.get('prescriptionId')?.setValue('');
      this.orderForm?.get('rxName')?.setValue('');
    }
    this.extractedUnverified = false;
    this.verified = true;
    const images = rx.images || [];
    const first = images[0] as any;
    this.sourceImageUrl =
      String((rx as any).sourceImageDataUrl || '').trim() ||
      first?.url ||
      first?.dataUrl ||
      '';
    this.syncExpirationDate();
    setTimeout(() => (this.ignoreVerifyReset = false));
  }

  private bindPrescriptionForm(form: FormGroup): void {
    if (!form.get('hide')) {
      form.addControl('hide', this.fb.control(false));
    }
    if (!form.get('voided')) {
      form.addControl('voided', this.fb.control(false));
    }
    if (!form.get('doctor')) {
      form.addControl('doctor', this.fb.control(''));
    }
    this.formGroup = form;
    const doctor =
      String(form.get('doctor')?.value || '').trim() ||
      rxDoctorName(form.getRawValue());
    form.get('doctor')?.setValue(doctor, { emitEvent: false });
    this.formSubscriptions.unsubscribe();
    this.formSubscriptions = new Subscription();
    this.formSubscriptions.add(
      this.rxNameControl.valueChanges.subscribe((value) => {
        this.formGroup.get('type')?.setValue(String(value || '').trim());
      })
    );
    this.formSubscriptions.add(
      this.formGroup.get('examDate')?.valueChanges.subscribe(() => {
        this.syncExpirationDate();
      })
    );
    this.formSubscriptions.add(
      this.formGroup.get('oneYearExpiration')?.valueChanges.subscribe(() => {
        this.syncExpirationDate();
      })
    );
    this.formSubscriptions.add(
      this.formGroup.valueChanges.subscribe(() => {
        if (this.ignoreVerifyReset || !this.verified) {
          return;
        }
        this.verified = false;
        this.extractedUnverified = true;
      })
    );
  }

  private syncExpirationDate(): void {
    const exam = this.formGroup.get('examDate')?.value;
    const oneYear = !!this.formGroup.get('oneYearExpiration')?.value;
    const expires = rxExpirationDate(exam, oneYear);
    if (expires) {
      this.formGroup.get('expirationDate')?.enable({ emitEvent: false });
      this.formGroup.get('expirationDate')?.setValue(expires, { emitEvent: false });
      this.formGroup.get('expirationDate')?.disable({ emitEvent: false });
    }
  }

  private syncRxOntoOrder(): void {
    const newName = String(this.newNameControl.value || '').trim();
    const name = newName || String(this.rxNameControl.value || this.formGroup.get('type')?.value || '').trim();
    this.formGroup.get('type')?.setValue(name);
    this.orderForm?.get('rxName')?.setValue(name);
    if (newName) {
      this.formGroup.get('_id')?.setValue('');
      this.formGroup.get('voided')?.setValue(false);
      this.orderForm?.get('prescriptionId')?.setValue('');
      this.rxNameControl.setValue(newName, { emitEvent: false });
      this.newNameControl.setValue('');
    }
    this.orderForm?.get('prescriptionId')?.setValue(
      this.formGroup.get('_id')?.value || ''
    );
  }

  private persistPrescriptionRecord(done: () => void): void {
    const patientId = String(this.patient?._id || '').trim();
    if (!patientId) {
      done();
      return;
    }
    const payload: any = {
      ...this.formGroup.getRawValue(),
      patientId,
      hide: !!this.formGroup.get('hide')?.value,
      voided: !!this.formGroup.get('voided')?.value,
      doctor: this.formGroup.get('doctor')?.value || '',
      sourceImageDataUrl: this.sourceImageUrl || '',
      extractedUnverified: this.extractedUnverified && !this.verified,
      verifiedBy: this.verified
        ? String(this.orderForm?.get('initials')?.value || '').trim()
        : '',
      verifiedAt: this.verified ? new Date() : null,
    };
    if (!payload._id) {
      delete payload._id;
    }
    delete payload.html;
    delete payload.eyeExam;
    if (!Array.isArray(payload.images) || !payload.images[0]) {
      delete payload.images;
    }
    const existingId = String(payload._id || '').trim();
    const request$: Observable<any> = existingId
      ? this.prescriptionService.updatePrescription(existingId, payload)
      : this.prescriptionService.addPrescription(payload);
    request$.subscribe({
      next: (saved: any) => {
        const id = saved?._id || saved?.prescriptionId || existingId;
        if (id) {
          this.formGroup.get('_id')?.setValue(id);
          this.orderForm?.get('prescriptionId')?.setValue(id);
        }
        this.loadPatientPrescriptions();
        done();
      },
      error: () => {
        this.notificationService.show(
          'error',
          'Could not save the RX record. The order was not updated.',
          'OK'
        );
      },
    });
  }

  private voidCurrentRx(): void {
    const id = String(this.formGroup.get('_id')?.value || '').trim();
    if (!id) {
      this.notificationService.show(
        'error',
        'No saved RX is selected to void.',
        'OK'
      );
      return;
    }
    this.dialog
      .open(RxConfirmDialogComponent, {
        width: '420px',
        data: {
          title: 'VOID THIS RX?',
          message:
            'VOID means this prescription did not work for the patient and must not be used again. The source image, values, notes, and linked jobs stay on file.',
          confirm: 'VOID',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.formGroup.get('voided')?.setValue(true);
        this.persistPrescriptionRecord(() => {
          this.notificationService.show(
            'success',
            'RX voided. It remains in history as Void - Do Not Use.',
            'OK'
          );
          this.startNewPrescription();
          this.loadPatientPrescriptions();
        });
      });
  }

  private deleteCurrentRx(): void {
    const id = String(this.formGroup.get('_id')?.value || '').trim();
    if (!id) {
      this.notificationService.show(
        'error',
        'No saved RX is selected to delete.',
        'OK'
      );
      return;
    }
    this.dialog
      .open(RxConfirmDialogComponent, {
        width: '420px',
        data: {
          title: 'DELETE THIS RX?',
          message:
            'DELETE permanently removes an RX entered into the wrong patient record or entered by mistake. This cannot be undone.',
          confirm: 'DELETE',
          requireTyped: 'DELETE',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          return;
        }
        this.prescriptionService.deletePrescription(id).subscribe({
          next: () => {
            this.notificationService.show('success', 'RX deleted.', 'OK');
            this.startNewPrescription();
            this.loadPatientPrescriptions();
          },
          error: () => {
            this.notificationService.show(
              'error',
              'Could not delete this RX.',
              'OK'
            );
          },
        });
      });
  }

  private refreshPendingRxTask(): void {
    const entries = this.currentEntries();
    const open = openTaskRows(entries).find((row) => {
      const status = canonicalOpenStatus(row.entry.status);
      return status === 'CALL / RX' || status === 'HOLD / RX';
    });
    if (open) {
      this.pendingRxTask = {
        kind: 'open',
        index: open.index,
        entry: open.entry,
        label: canonicalOpenStatus(open.entry.status) === 'HOLD / RX' ? 'HOLD / RX' : 'CALL FOR RX',
        actions: openTaskActions(open.entry.status, open.entry),
      };
      this.taskWhoToCall.setValue(open.entry.whoToCall || '', { emitEvent: false });
      this.taskStatusChoice.setValue(
        open.entry.activityChoice || 'CALL NEEDED',
        { emitEvent: false }
      );
      this.hydrateRxFromPendingTask(open.entry);
      return;
    }
    const awaiting = awaitingRows(entries).find(
      (row) => String(row.entry.status || '').toUpperCase() === 'AWAITING RX'
    );
    if (awaiting) {
      this.pendingRxTask = {
        kind: 'awaiting',
        index: awaiting.index,
        entry: awaiting.entry,
        label: 'PT. TO SEND',
        actions: awaitingActions(awaiting.entry.status),
      };
      this.taskStatusChoice.setValue('', { emitEvent: false });
      this.hydrateRxFromPendingTask(awaiting.entry);
      return;
    }
    this.pendingRxTask = null;
  }

  private hydrateRxFromPendingTask(entry: WorkflowEntry): void {
    if (!this.formGroup) {
      return;
    }
    const exam = entry?.expectedDate;
    if (exam && !this.formGroup.get('examDate')?.value) {
      this.formGroup.get('examDate')?.setValue(exam);
    }
    const notes = String(entry?.notes || '').trim();
    if (notes && !String(this.formGroup.get('notes')?.value || '').trim()) {
      this.formGroup.get('notes')?.setValue(notes);
    }
  }

  private patchAwaitingExamDate(): void {
    const activity = this.activityFormArray;
    if (!activity || !this.pendingRxTask) {
      return;
    }
    const group = activity.at(this.pendingRxTask.index) as FormGroup;
    const exam = this.formGroup.get('examDate')?.value;
    if (group?.get('expectedDate') && exam) {
      group.get('expectedDate').setValue(exam);
    }
    if (group?.get('followUpDate') && exam) {
      group.get('followUpDate').setValue(exam);
    }
    if (group?.get('notes')) {
      group.get('notes').setValue(this.formGroup.get('notes')?.value || '');
    }
  }

  private maybeCompleteNeedsRxTask(done: () => void): void {
    if (!this.pendingRxTask) {
      done();
      return;
    }
    this.dialog
      .open(RxConfirmDialogComponent, {
        width: '420px',
        data: {
          title: 'RX RECEIVED?',
          message: 'Confirm RX received and complete Needs RX task?',
          confirm: 'COMPLETE TASK',
        },
      })
      .afterClosed()
      .subscribe((confirmed) => {
        if (!confirmed) {
          done();
          return;
        }
        const initials = normalizeInitials(
          this.orderForm?.get('initials')?.value || this.defaultInitials
        );
        const entries = this.currentEntries();
        const input: CompleteTaskInput = {
          id: this.pendingRxTask.entry.id,
          index: this.pendingRxTask.index,
          initials,
          notes: this.formGroup.get('notes')?.value,
          activityChoice: 'RX RECEIVED',
        };
        const result =
          this.pendingRxTask.kind === 'open'
            ? completeOpenTask(entries, input, this.workflowContext())
            : resolveAwaiting(entries, input, this.workflowContext());
        this.commitTaskResult(result, initials);
      });
  }

  private get activityFormArray(): FormArray {
    return this.orderForm?.get('products.0.frame.activity') as FormArray;
  }

  private currentEntries(): WorkflowEntry[] {
    return (this.activityFormArray?.getRawValue() || []).map((entry) =>
      activityEntryDefaults(entry)
    );
  }

  private workflowContext(): WorkflowContext {
    return {
      jobActions: jobActionList(
        this.orderForm?.get('jobAction')?.value,
        this.orderForm?.get('jobActions')?.value
      ),
      status: this.orderForm?.get('status')?.value,
      physicalStatus: this.orderForm?.get('products.0.frame.physicalStatus')?.value,
      lensStatus: this.orderForm?.get('lensStatus')?.value,
      frameOnly: !!this.orderForm?.get('frameOnly')?.value,
      followUpDate: this.orderForm?.get('products.0.frame.followUpDate')?.value,
      awaitingRxFollowUpHours: this.settingsService.awaitingRxFollowUpHours,
    };
  }

  private commitTaskResult(result: WorkflowResult, initials: string): void {
    this.writeEntries(result.entries);
    const patch = result.patch;
    if (patch?.rxStatus !== undefined) {
      this.orderForm?.get('rxStatus')?.setValue(patch.rxStatus);
    }
    if (patch?.status) {
      this.orderForm?.get('status')?.setValue(patch.status);
    }
    if (patch?.todo) {
      this.orderForm?.get('todo')?.setValue(patch.todo);
    }
    if (patch?.followUpDate !== undefined) {
      this.orderForm?.get('products.0.frame.followUpDate')?.setValue(patch.followUpDate);
    }
    if (Array.isArray(patch?.jobActionsRemove) && patch.jobActionsRemove.length) {
      let actions = jobActionList(
        this.orderForm?.get('jobAction')?.value,
        this.orderForm?.get('jobActions')?.value
      );
      patch.jobActionsRemove.forEach((action) => {
        actions = toggleJobAction(actions, action, false);
      });
      this.orderForm?.get('jobActions')?.setValue(actions);
      this.orderForm?.get('jobAction')?.setValue(exclusiveJobAction(actions));
    }
    this.orderForm?.get('initials')?.setValue(initials);
    this.refreshPendingRxTask();
    this.saveOrder.emit({ destination: 'SAVE' });
  }

  private writeEntries(entries: WorkflowEntry[]): void {
    const activity = this.activityFormArray;
    if (!activity) {
      return;
    }
    while (activity.length) {
      activity.removeAt(0);
    }
    entries.forEach((entry) => {
      const value = activityEntryDefaults(entry);
      activity.push(
        this.fb.group({
          id: value.id,
          kind: value.kind,
          date: value.date,
          status: value.status,
          branch: value.branch,
          notes: value.notes,
          repairItems: [value.repairItems || []],
          waitingOn: value.waitingOn,
          vendor: value.vendor,
          followUpDate: value.followUpDate,
          expectedDate: value.expectedDate,
          followUpNumber: value.followUpNumber,
          activityChoice: value.activityChoice,
          reopenedFrom: value.reopenedFrom,
          sourceId: value.sourceId,
          description: value.description,
          referenceNumber: value.referenceNumber,
          carrier: value.carrier,
          trackingNumber: value.trackingNumber,
          labVendor: value.labVendor,
          repairLocation: value.repairLocation,
          estimate: value.estimate,
          estimateApprovalRequired: value.estimateApprovalRequired,
          approvalStatus: value.approvalStatus,
          otherText: value.otherText,
          whoToCall: value.whoToCall,
          images: [value.images || []],
          eta: value.eta,
          completed: value.completed,
          completedBy: value.completedBy,
          completedAt: value.completedAt,
        })
      );
    });
    activity.markAsDirty();
  }

  private printUploadedImage(): void {
    if (!this.sourceImageUrl) {
      this.notificationService.show('error', 'No RX image to print.', 'OK');
      return;
    }
    const WindowPrt = window.open('', '', 'left=0,top=0,width=900,height=900');
    if (!WindowPrt) {
      return;
    }
    WindowPrt.document.write(
      `<html><body style="margin:0;text-align:center;"><img src="${this.sourceImageUrl}" style="max-width:100%;max-height:100vh"></body></html>`
    );
    WindowPrt.document.close();
    WindowPrt.focus();
    WindowPrt.print();
  }

  private hasStructuredValues(): boolean {
    const right = this.formGroup?.get('right')?.value || {};
    const left = this.formGroup?.get('left')?.value || {};
    return !!(right.sph || right.cyl || left.sph || left.cyl);
  }
}
